import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, ListItem } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { CreatePage, FuseInput, FuseSelect, LoanSelect } from '/src/components';
import { useAssetStore, useFilterStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { AssetType, CreateAssetForm, DataModelType, FormErrors } from '/src/lib/types';
import * as utils from '/src/utils';

/**
 * Create Asset Page
 */
export const CreateAssetPage: React.FC = observer(() => {
  /** Context **/
  const assetStore = useAssetStore();
  const filterStore = useFilterStore();
  const navigate = useNavigate();

  /** State **/
  const [formValues, setFormValues] = useState<CreateAssetForm>({
    name: '',
    effectiveDate: new Date(),
    value: '',
    paymentSchedule: '',
    files: [],
    companyComments: [],
    socketComments: [],
    adminFee: '0',
    discountFee: '0',
    lateFee: '0',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /** Computed **/
  const loans = useMemo(() => filterStore.loanListGet(), [filterStore._loanList]);

  const projectedValue = useMemo(() => {
    const value = parseFloat(formValues.value || '0');
    const currentAdjustments = parseFloat(formValues.currentAdjustments || '0');
    const advanceRate = formValues.loan?.advanceRate || 0;
    const adminFee = formValues.loan?.adminFee || 0;
    const discountFee = formValues.loan?.discountFee || 0;
    const lateFee = parseFloat(formValues.lateFee || '0');
    const dateDiff =
      formValues.nextDate && formValues.effectiveDate
        ? dayjs(formValues.nextDate).diff(dayjs(formValues.effectiveDate), 'days') / 30
        : 0;

    const res =
      value + currentAdjustments - value * advanceRate - value * adminFee - value * discountFee * dateDiff - lateFee;

    return !Number.isNaN(res) ? res : 0;
  }, [
    formValues.value,
    formValues.currentAdjustments,
    formValues.loan,
    formValues.lateFee,
    formValues.nextDate,
    formValues.effectiveDate,
  ]);

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));

    // Clear error when field is updated
    if (formErrors[key]) {
      setFormErrors((prev) => ({
        ...prev,
        [key]: '',
      }));
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = () => {
    const errors: FormErrors = {};

    if (!formValues.loan) errors.loan = 'Loan is required';
    if (!formValues.assetType) errors.assetType = 'Type is required';
    if (!formValues.name) errors.name = 'Name is required';
    if (!utils.isNumber(formValues.value)) errors.value = 'Valid amount is required';
    if (!formValues.program) errors.program = 'Program is required';
    if (!formValues.paymentSchedule) errors.paymentSchedule = 'Schedule is required';
    if (!formValues.nextDate) errors.nextDate = 'Date is required';
    if (!formValues.effectiveDate) errors.effectiveDate = 'Date is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Creates an Asset.
   *
   * @remarks
   * Redirects to the new Asset's Detail page afterwards.
   */
  const create = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors first.');
      return;
    }

    try {
      const response = await assetStore.create(formValues);
      if (!response) throw new Error(`Could not create Asset with data: ${JSON.stringify(formValues)}`);

      toast.success(toastMessages.createAsset.success);
      navigate(`/deals/${response._id}`);
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.createAsset.error);
    }
  };

  /** Render **/
  return (
    <CreatePage modelType={DataModelType.Asset} onCreate={create}>
      {/* Loan */}
      <LoanSelect
        id="loan"
        value={formValues.loan?._id ?? ''}
        setValue={(loanId) => updateFormValue('loan')(loans.find((loan) => loan._id === loanId))}
        loans={loans}
        required
        error={formErrors.loan}
      />

      <Box display="flex" gap="16px">
        {/* Asset Type */}
        <Box display="flex" flex={33}>
          <FuseSelect
            label="Asset Type"
            options={Object.entries(AssetType).map(([label, value]) => ({ label, value }))}
            value={formValues?.assetType}
            updateValue={updateFormValue('assetType')}
            placeholder="Asset Type"
            flex="33"
            required
            error={formErrors.assetType}
          />
        </Box>

        {/* Asset Name */}
        <Box display="flex" flex={66}>
          <FuseInput
            label="Asset Name"
            field="name"
            formValues={formValues}
            updateValue={updateFormValue('name')}
            required
            error={formErrors.name}
          />
        </Box>
      </Box>

      <Box display="flex" gap="16px">
        {/* Value */}
        <FuseInput
          label="Value"
          field={formValues.value} // have to parse this as a float at some point
          formValues={formValues}
          updateValue={updateFormValue('value')}
          type="number"
          moneyMask
          negativeNumber
          required
          error={formErrors.value}
        />

        {/* Program */}
        <FuseInput
          label="Program"
          field="program"
          formValues={formValues}
          updateValue={updateFormValue('program')}
          required
          error={formErrors.program}
        />

        {formValues.loan?.isOwned && (
          <ListItem className="no-pad-sides textZoomIn" sx={{ display: 'flex' }}>
            <Box sx={{ flex: 1 }} layout-align="center center">
              <Box fontWeight="100">
                Expected Residual Payment:{' '}
                <Box id="expectedResidualPayment" className="strong">
                  {utils.currency.format(projectedValue)}
                </Box>
              </Box>
            </Box>
          </ListItem>
        )}
      </Box>

      <Box display="flex" gap="16px">
        {/* Payment Schedule */}
        <FuseInput
          label="Payment Schedule"
          field="paymentSchedule"
          formValues={formValues}
          updateValue={updateFormValue('paymentSchedule')}
          required
          error={formErrors.paymentSchedule}
        />

        {/* Next Payment */}
        <FuseInput
          label="Next Payment"
          value={formValues.nextDate}
          updateValue={updateFormValue('nextDate')}
          type="date"
          required
          error={formErrors.nextDate}
        />
      </Box>

      {/* Asset Effective Date */}
      <FuseInput
        label="Asset Effective Date"
        value={formValues.effectiveDate}
        updateValue={updateFormValue('effectiveDate')}
        type="date"
        required
        error={formErrors.effectiveDate}
        boxProps={{ className: 'pad-bottom-xs' }}
      />
    </CreatePage>
  );
});
